import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GlassCard,
  CardHeader,
  Button,
  KpiCard,
  DonutChart,
  Funnel,
  LineAreaChart,
  StatusPill,
  ProgressBar,
  Icon,
  chart,
} from '../../campaign-os';
import { usePageChrome } from '../PageChrome';

import { useWizard } from '../store/wizardStore';
import { campaignService } from '../../../../services/apiService';
import { useApiData } from '../data/useApiData';
import { ChannelReach } from '../components/ChannelReach';
import { totalReach } from '../data/channelChart';
import { mapCampaigns } from '../data/mapCampaign';
import { formatShortDate } from '../data/format';

/**
 * Campaign Dashboard — wired to the real campaigns read model.
 *
 *   GET /campaigns/summary  → KPI strip, activity chart, status donut, funnel, open-rate trend
 *   GET /campaigns?limit=6  → "Recent Campaigns" table (newest first)
 *
 * Every figure on this screen comes from those two responses (or is derived from them);
 * nothing is hard-coded and there is no demo data on this screen.
 */
const ZERO_CAMPAIGNS = { total: 0, queued: 0, sending: 0, sent: 0, failed: 0 };
const ZERO_RECIPIENTS = {
  total_count: 0,
  sent_count: 0,
  delivered_count: 0,
  opened_count: 0,
  clicked_count: 0,
  bounced_count: 0,
  failed_count: 0,
};
const ZERO_RATES = { delivery: 0, open: 0, click: 0, bounce: 0 };
const ZERO_REGULARIZATION = {
  pending_batches: 0,
  completed_batches: 0,
  expired_requests: 0,
  requests_sent: 0,
  requests_submitted: 0,
  requests_verified: 0,
  requests_total: 0,
};
const FUNNEL_STAGES = ['Recipients', 'Sent', 'Delivered', 'Opened', 'Hard bounces'];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

/** Donut slice colours: status tokens where they exist, cos-status-coral (tailwind.config.cjs) for Failed. */
const STATUS_SLICE_COLOR = {
  Completed: chart.status.Completed,
  Sending: chart.status.Running,
  Failed: '#A35A4E',
};

/** The screen re-reads itself on this cadence; delivery events arrive by provider webhook. */
const LIVE_REFRESH_MS = 60_000;
const clock = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

export function DashboardPage() {

  const { open } = useWizard();

  const summary = useApiData(() => campaignService.summary(), [], { cacheKey: 'campaign:summary' });
  const recent = useApiData(() => campaignService.list({ limit: 6 }), [], { cacheKey: 'campaign:list:6' });
  const [updatedAt, setUpdatedAt] = useState(() => new Date());

  // Live: a campaign launched or reconciled anywhere shows up here without a reload.
  const refreshAll = useCallback(() => {
    summary.refetch();
    recent.refetch();
    setUpdatedAt(new Date());
  }, [summary.refetch, recent.refetch]);
  useEffect(() => {
    const t = setInterval(refreshAll, LIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [refreshAll]);

  usePageChrome(
    () => ({
      title: 'Campaign Dashboard',
      subtitle: `Live campaign metrics · updated ${clock(updatedAt)} · refreshes every minute`,
      actions: (
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon="refresh" onClick={refreshAll}>
            Refresh
          </Button>
          <Button variant="primary" icon="add" onClick={() => open()}>
            New Campaign
          </Button>
        </div>
      ),
    }),
    [open, refreshAll, updatedAt]
  );

  // ---- Normalise the summary payload (zero-filled so the page never shows fabricated numbers) ----
  const s = summary.data?.data || null;
  const campaigns = { ...ZERO_CAMPAIGNS, ...(s?.campaigns || {}) };
  const recipients = { ...ZERO_RECIPIENTS, ...(s?.recipients || {}) };
  // Copies are ordinary message rows, so the portfolio splits by address field the same
  // way one campaign does. Absent entirely when no campaign has ever used CC or BCC.
  const channels = Array.isArray(s?.channels) ? s.channels : [];
  const channelReach = totalReach(channels);
  const rates = { ...ZERO_RATES, ...(s?.rates || {}) };
  const regularization = { ...ZERO_REGULARIZATION, ...(s?.regularization || {}) };
  const activity = Array.isArray(s?.activity) ? s.activity : [];
  const hasData = s?.has_data === true;
  const sendingUnconfigured = s?.sending_configured === false;

  const funnel = useMemo(() => {
    const stages = Array.isArray(s?.funnel) && s.funnel.length > 0 ? s.funnel : FUNNEL_STAGES.map((stage) => ({ stage, value: 0 }));
    const sent = Number(s?.recipients?.sent_count || 0);
    // Hard bounces are not a step recipients converted through, so the bar names its own
    // denominator and wears the one alarming colour on the card.
    return stages.map((stage) =>
      stage.stage === 'Hard bounces'
        ? { ...stage, tone: 'alarm', pctLabel: sent > 0 ? `${Math.round((stage.value / sent) * 1000) / 10}% of sent` : '—' }
        : stage
    );
  }, [s]);

  const inFlight = campaigns.queued + campaigns.sending;

  // Mailboxes that refused for good. The API reads them off the message rows; the funnel it
  // sends carries the same number, so the card and the last bar can never disagree.
  const hardBounced = Number(s?.hard_bounced_count || 0);
  const hardBounceRate = recipients.sent_count > 0 ? Math.round((hardBounced / recipients.sent_count) * 1000) / 10 : 0;
  const bounceBadge = recipients.sent_count > 0 ? `${hardBounceRate}% of sent` : '—';

  const sentMoM = useMemo(() => monthOverMonth(activity, 'sent'), [activity]);

  const statusMix = useMemo(
    () =>
      [
        { name: 'Completed', value: campaigns.sent },
        { name: 'Sending', value: inFlight },
        { name: 'Failed', value: campaigns.failed },
      ].filter((d) => d.value > 0),
    [campaigns.sent, campaigns.failed, inFlight]
  );

  const kpis = [
    {
      key: 'total',
      label: 'Total campaigns',
      value: fmt(campaigns.total),
      caption: `${fmt(campaigns.sent)} completed · ${fmt(campaigns.failed)} failed`,
      icon: 'campaign',
      tone: 'teal',
    },
    {
      key: 'sending',
      label: 'Sending now',
      value: fmt(inFlight),
      caption: 'Queued or in flight',
      icon: 'play_circle',
      tone: 'teal',
    },
    {
      key: 'recipients',
      label: 'Recipients',
      value: fmt(recipients.total_count),
      caption: `${fmt(recipients.sent_count)} sent`,
      icon: 'groups',
      tone: 'teal',
    },
    {
      key: 'delivered',
      label: 'Delivered',
      value: fmt(recipients.delivered_count),
      caption: `${rates.delivery}% delivery rate`,
      icon: 'mark_email_read',
      tone: 'neutral',
    },
    {
      key: 'opened',
      label: 'Opened',
      value: fmt(recipients.opened_count),
      caption: `${rates.open}% open rate`,
      icon: 'drafts',
      tone: 'beige',
    },
    {
      key: 'hard-bounces',
      label: 'Hard bounces',
      value: fmt(hardBounced),
      caption: `${hardBounceRate}% of ${fmt(recipients.sent_count)} sent`,
      icon: 'block',
      tone: 'coral',
    },
  ];

  // ---- Recent campaigns: every row is a real send from GET /campaigns ----
  const recentRows = useMemo(() => mapCampaigns(recent.data?.data), [recent.data]);

  if (summary.loading && !summary.data) return <DashboardSkeleton />;
  if (summary.error && !summary.data) return <ErrorCard message={summary.error} onRetry={summary.refetch} />;

  return (
    <div className="space-y-5">
      {summary.error && <ErrorCard message={summary.error} onRetry={summary.refetch} />}

      {!hasData && (
        <EmptyState
          icon="forward_to_inbox"
          title="No campaign activity yet"
          text="Real delivery, open and click metrics appear here after the first bulk send. Send a bulk rectification link from Regularization to start."
          action={
            <Link
              to="/campaigning-tool/regularization"
              className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-cos-line bg-white/65 px-4 text-sm font-semibold text-cos-ink transition-all duration-200 hover:border-cos-teal/35 hover:bg-cos-surface-hover hover:text-cos-teal-deep"
            >
              Go to Regularization <Icon name="arrow_forward" className="text-[18px]" weight={500} />
            </Link>
          }
        />
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => (
          <KpiCard key={k.key} label={k.label} value={k.value} caption={k.caption} icon={k.icon} tone={k.tone} />
        ))}
      </div>

      {sendingUnconfigured && (
        <p className="flex items-start gap-1.5 text-[12px] leading-tight text-cos-ink-muted">
          <Icon name="info" className="mt-px text-[15px]" />
          No campaign email transport is configured on this server — campaigns cannot be sent, regularization
          emails fall back to one-by-one SMTP, and delivery tracking is unavailable.
        </p>
      )}

      {/* Analytical grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Campaign activity */}
        <GlassCard accentRail className="relative lg:col-span-8">
          <CardHeader
            title="Campaign Activity"
            subtitle="Messages sent vs. opened, last 6 months"
            action={<MoMPill delta={sentMoM} />}
          />
          <LineAreaChart
            data={activity}
            xKey="label"
            height={248}
            series={[
              { key: 'sent', name: 'Sent', color: chart.bronze, area: true },
              { key: 'opened', name: 'Opened', color: chart.teal, area: true },
            ]}
          />
        </GlassCard>

        {/* Campaign status */}
        <GlassCard className="lg:col-span-4">
          <CardHeader title="Campaign Status" subtitle="Portfolio distribution" />
          <div className="pt-2">
            <DonutChart
              data={statusMix}
              colorFor={(name) => STATUS_SLICE_COLOR[name]}
              centerValue={fmt(campaigns.total)}
              centerLabel="Total"
              height={188}
            />
          </div>
        </GlassCard>

        {/* Vendor response funnel */}
        <GlassCard className="lg:col-span-5">
          <CardHeader
            title="Vendor Response Funnel"
            subtitle="Recipients → Sent → Delivered → Opened → Hard bounces"
            action={<span className="mono text-2xs font-semibold text-cos-ink-muted">{bounceBadge}</span>}
          />
          <div className="pt-1">
            <Funnel data={funnel} />
          </div>
        </GlassCard>

        {/* Open rate trend */}
        <GlassCard className="lg:col-span-7">
          <CardHeader
            title="Open Rate"
            subtitle="Monthly trend, last 6 months"
            action={
              <div className="text-right">
                <div className="mono text-xl font-bold leading-none text-cos-ink">{rates.open}%</div>
                <div className="text-2xs text-cos-ink-secondary">
                  {fmt(recipients.opened_count)} of {fmt(recipients.delivered_count)} delivered
                </div>
              </div>
            }
          />
          <LineAreaChart
            data={activity}
            xKey="label"
            height={200}
            yWidth={32}
            yFormatter={(v) => `${v}%`}
            series={[{ key: 'open_rate', name: 'Open rate', color: chart.teal, area: true }]}
          />
        </GlassCard>

        {/* Recent campaigns table */}
        {/* Reach by address field — the portfolio's copies, counted like any other row */}
        {channels.length > 0 && (
          <GlassCard className="lg:col-span-12">
            <CardHeader
              title="Reach by address field"
              subtitle="To, CC and BCC across every campaign"
              action={
                <span className="text-2xs text-cos-ink-secondary">
                  <span className="mono font-bold text-cos-ink">{fmt(channelReach)}</span> addresses in all
                </span>
              }
            />
            <ChannelReach channels={channels} split />
          </GlassCard>
        )}

        <GlassCard className="lg:col-span-12" padded={false}>
          <div className="flex items-center justify-between px-5 pb-3 pt-5">
            <CardHeader title="Recent Campaigns" subtitle="Latest sends, newest first" className="mb-0" />
            <Link
              to="/campaigning-tool/campaigns"
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-cos-teal-deep hover:underline"
            >
              View all <Icon name="arrow_forward" className="text-[16px]" />
            </Link>
          </div>
          <div className="overflow-x-auto px-2 pb-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cos-line">
                  {['Campaign', 'Status', 'Recipients', 'Progress', 'Response', 'Sent'].map((h, i) => (
                    <th
                      key={h}
                      className={`whitespace-nowrap px-3 py-2.5 text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted ${
                        i > 1 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.loading && !recent.data && (
                  <TableNote>Loading campaigns…</TableNote>
                )}
                {recent.error && (
                  <TableNote>
                    <span className="text-cos-status-coral">{recent.error}</span>{' '}
                    <button type="button" onClick={recent.refetch} className="font-semibold text-cos-teal-deep hover:underline">
                      Retry
                    </button>
                  </TableNote>
                )}
                {!recent.loading && !recent.error && recentRows.length === 0 && (
                  <TableNote>No campaigns yet — the first bulk send will appear here.</TableNote>
                )}
                {recentRows.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-cos-line-subtle transition-colors last:border-0 hover:bg-[rgba(13,168,155,0.05)]"
                  >
                    <td className="px-3 py-3">
                      <div className="font-semibold text-cos-ink">{c.name}</div>
                      <div className="mono text-2xs text-cos-ink-muted">
                        {c.id} · {c.templateName}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <StatusPill status={c.status} />
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="mono text-cos-ink">{fmt(c.audienceSize)}</div>
                      <div className="text-2xs text-cos-ink-muted">{c.audience}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="ml-auto w-36">
                        <ProgressBar value={c.progress} showLabel />
                      </div>
                    </td>
                    <td
                      className="mono px-3 py-3 text-right font-semibold text-cos-ink"
                      title={c.responseLabel || 'Response rate'}
                    >
                      {c.responseRate ? `${c.responseRate}%` : '—'}
                    </td>
                    <td className="mono px-3 py-3 text-right text-cos-ink-secondary">
                      {c.created ? formatShortDate(c.created) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

export default DashboardPage;

// ---------------------------------------------------------------------------------------------
// Local helpers (presentational)
// ---------------------------------------------------------------------------------------------

/** Month-over-month % change of `key` between the last two points; null when not computable. */
function monthOverMonth(series, key) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const prev = Number(series[series.length - 2]?.[key] || 0);
  const last = Number(series[series.length - 1]?.[key] || 0);
  if (prev <= 0) return null;
  return ((last - prev) / prev) * 100;
}

function MoMPill({ delta }) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;
  const up = delta >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-semibold ${
        up ? 'bg-cos-teal-faint text-cos-teal-deep' : 'bg-cos-status-coral-bg text-cos-status-coral'
      }`}
    >
      <Icon name={up ? 'trending_up' : 'trending_down'} className="text-[14px]" />
      {up ? '+' : ''}
      {delta.toFixed(0)}% MoM
    </span>
  );
}



function TableNote({ children }) {
  return (
    <tr>
      <td colSpan={6} className="px-3 py-6 text-center text-[13px] text-cos-ink-muted">
        {children}
      </td>
    </tr>
  );
}

function EmptyState({ icon, title, text, action }) {
  return (
    <GlassCard className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cos-teal-faint text-cos-teal-deep">
        <Icon name={icon} className="text-[26px]" weight={500} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-bold leading-tight tracking-tight text-cos-ink">{title}</h3>
        <p className="mt-1 text-[13px] leading-snug text-cos-ink-secondary">{text}</p>
      </div>
      {action}
    </GlassCard>
  );
}

function ErrorCard({ message, onRetry }) {
  return (
    <GlassCard className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-cos-status-coral-bg text-cos-status-coral">
        <Icon name="error" className="text-[26px]" weight={500} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-[15px] font-bold leading-tight tracking-tight text-cos-ink">Couldn&apos;t load campaign metrics</h3>
        <p className="mt-1 text-[13px] leading-snug text-cos-ink-secondary">{message}</p>
      </div>
      <Button variant="secondary" icon="refresh" onClick={onRetry}>
        Retry
      </Button>
    </GlassCard>
  );
}

function DashboardSkeleton() {
  return (
    <div className="animate-pulse space-y-5" aria-busy="true" aria-label="Loading campaign dashboard">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="glass-card h-[122px]" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="glass-card h-[320px] lg:col-span-8" />
        <div className="glass-card h-[320px] lg:col-span-4" />
        <div className="glass-card h-[280px] lg:col-span-5" />
        <div className="glass-card h-[280px] lg:col-span-7" />
        <div className="glass-card h-[240px] lg:col-span-12" />
      </div>
    </div>
  );
}
